import type { FormEvent } from "react";
import { useEffect, useMemo, useState } from "react";
import type {
  AdminLessonDetailDto,
  AdminMembersResponseDto,
  LessonStatus,
  MemberRole,
  StudioDecisionStatus,
  TeacherAssignmentStatus,
  StudiosResponseDto
} from "@madamuh/domain";
import { getRecurrencePresetLabel } from "@madamuh/domain";
import { Link, useNavigate, useParams } from "react-router-dom";
import { fetchAdminMembers, fetchAdminStudios } from "../../shared/api/adminDirectory";
import { ApiError } from "../../shared/api/client";
import {
  cancelAdminManualOccurrence,
  createAdminManualOccurrence,
  fetchAdminLesson,
  patchAdminLesson,
  transitionAdminLesson
} from "../../shared/api/adminLesson";
import { fetchStudentBillingLessonFee, type StudentBillingLessonFeeResponse } from "../../shared/api/studentBillingConfiguration";
import {
  formatTenantDate,
  formatTenantIsoDate,
  formatTenantIsoTime,
  formatTenantMonthHeading
} from "../../shared/lib/tenantDateTime";
import type { TenantLabels } from "../../shared/lib/tenantLabels";
import { GENERIC_TENANT_LABELS, getTenantLabels } from "../../shared/lib/tenantLabels";
import { describeStatus } from "../../shared/lib/uiLabels";
import {
  getProtectedApiErrorMessage,
  isUnauthenticatedApiError
} from "../../shared/session/protectedApiError";
import { useProtectedRouteSession } from "../../shared/session/useProtectedRouteSession";
import { OperatorPageScaffold } from "../../shared/ui/protected/OperatorPageScaffold";

function buildAdminLinks(labels: TenantLabels) {
  return [
  { to: "/t/:tenantSlug/teacher/admin", label: "管理ホーム" },
  { to: "/t/:tenantSlug/teacher/admin/lessons", label: `${labels.lesson}一覧` }
  ];
}

const WEEKDAY_LABELS = ["日", "月", "火", "水", "木", "金", "土"] as const;

type LessonEditState = {
  title: string;
  shortTitle: string;
  description: string;
  genre: string;
  targetLabel: string;
  publicVisible: boolean;
  trialPolicyId: string;
  visibilityPolicyId: string;
  sortOrder: string;
};

type ManualOccurrenceFormState = {
  occurrenceDate: string;
  startTime: string;
  endTime: string;
  studioId: string;
  studioDecisionStatus: StudioDecisionStatus;
  teacherPlatformUserIds: string[];
  teacherAssignmentStatus: TeacherAssignmentStatus;
  publicNote: string;
  operatorNote: string;
  adminNote: string;
};

function toEditState(lesson: AdminLessonDetailDto): LessonEditState {
  return {
    title: lesson.title,
    shortTitle: lesson.shortTitle ?? "",
    description: lesson.description ?? "",
    genre: lesson.genre ?? "",
    targetLabel: lesson.targetLabel ?? "",
    publicVisible: lesson.publicVisible,
    trialPolicyId: lesson.trialPolicyId ?? "",
    visibilityPolicyId: lesson.visibilityPolicyId ?? "",
    sortOrder: String(lesson.sortOrder)
  };
}

function createInitialManualOccurrenceFormState(lesson?: AdminLessonDetailDto | null): ManualOccurrenceFormState {
  const firstSlot = lesson?.recurringSlots[0] ?? null;
  const defaultStudioDecisionStatus: StudioDecisionStatus =
    lesson?.defaultStudioDecisionStatus === "confirmed" || lesson?.defaultStudioDecisionStatus === "tbd"
      ? lesson.defaultStudioDecisionStatus
      : firstSlot?.studioDecisionStatus === "confirmed" || firstSlot?.studioDecisionStatus === "tbd"
        ? firstSlot.studioDecisionStatus
        : "tbd";
  const defaultTeacherAssignmentStatus: TeacherAssignmentStatus =
    lesson?.defaultTeacherAssignmentStatus === "assigned" || lesson?.defaultTeacherAssignmentStatus === "tbd"
      ? lesson.defaultTeacherAssignmentStatus
      : firstSlot?.teacherAssignmentStatus === "assigned" || firstSlot?.teacherAssignmentStatus === "tbd"
        ? firstSlot.teacherAssignmentStatus
        : "tbd";

  return {
    occurrenceDate: firstSlot?.activeFrom ?? lesson?.startDate ?? "",
    startTime: lesson?.defaultStartTime ?? firstSlot?.startTime ?? "10:00",
    endTime: lesson?.defaultEndTime ?? firstSlot?.endTime ?? "11:30",
    studioId: lesson?.defaultStudioId ?? firstSlot?.studioId ?? "",
    studioDecisionStatus: defaultStudioDecisionStatus,
    teacherPlatformUserIds:
      lesson?.defaultTeacherPlatformUserIds.length
        ? lesson.defaultTeacherPlatformUserIds
        : (firstSlot?.defaultTeacherPlatformUserIds ?? []),
    teacherAssignmentStatus: defaultTeacherAssignmentStatus,
    publicNote: "",
    operatorNote: "",
    adminNote: ""
  };
}

function hasTeacherRole(roles: readonly MemberRole[]): boolean {
  return roles.includes("teacher") || roles.includes("admin");
}

function getMemberDisplayName(member: AdminMembersResponseDto["items"][number]): string {
  return member.appNickname ?? member.lineDisplayNameSnapshot ?? member.platformUserId;
}

function toggleSelection(current: readonly string[], value: string, checked: boolean): string[] {
  const next = new Set(current);

  if (checked) {
    next.add(value);
  } else {
    next.delete(value);
  }

  return [...next];
}

function getAvailableTransitions(status: LessonStatus): LessonStatus[] {
  switch (status) {
    case "draft":
      return ["active"];
    case "active":
      return ["paused", "ended"];
    case "paused":
      return ["active", "ended"];
    case "ended":
      return ["archived"];
    default:
      return [];
  }
}

function formatOpenEndedDate(value: string | null): string {
  return value === "9999-12-31" ? "終了日未設定" : formatTenantDate(value);
}

function formatLessonDateRange(startDate: string | null, endDate: string | null): string {
  return `${formatTenantDate(startDate)}〜${formatOpenEndedDate(endDate)}`;
}

function formatYearMonthLinkLabel(yearMonth: string): string {
  return `${formatTenantMonthHeading(yearMonth)}のスケジュール`;
}

export function AdminLessonDetailPage() {
  const navigate = useNavigate();
  const { tenantSlug = "unknown-tenant", lessonId = "" } = useParams();
  const { routeStatus, sessionStatus, session, errorMessage, isSessionReady } = useProtectedRouteSession(tenantSlug);
  const [lesson, setLesson] = useState<AdminLessonDetailDto | null>(null);
  const [billingFee, setBillingFee] = useState<StudentBillingLessonFeeResponse | null>(null);
  const [billingFeeUnavailable, setBillingFeeUnavailable] = useState(false);
  const [members, setMembers] = useState<AdminMembersResponseDto | null>(null);
  const [studios, setStudios] = useState<StudiosResponseDto | null>(null);
  const [editState, setEditState] = useState<LessonEditState | null>(null);
  const [manualOccurrenceForm, setManualOccurrenceForm] = useState<ManualOccurrenceFormState>(
    createInitialManualOccurrenceFormState()
  );
  const [loadState, setLoadState] = useState<"idle" | "loading" | "error">("idle");
  const [busyKey, setBusyKey] = useState<string | null>(null);
  const [loadError, setLoadError] = useState<string | null>(null);
  const [feedbackMessage, setFeedbackMessage] = useState<string | null>(null);
  const [lastManualOccurrenceYearMonth, setLastManualOccurrenceYearMonth] = useState<string | null>(null);
  const hasAdminRole = Boolean(session?.member?.roles.includes("admin"));
  const tenantLabels = session ? getTenantLabels(session.tenant.uiLabelProfile) : GENERIC_TENANT_LABELS;
  const transitions = useMemo(() => (lesson ? getAvailableTransitions(lesson.status) : []), [lesson]);
  const teacherMembers = useMemo(
    () => members?.items.filter((member) => hasTeacherRole(member.roles)) ?? [],
    [members]
  );

  async function refreshLesson() {
    setLoadState("loading");
    setLoadError(null);
    setFeedbackMessage(null);

    try {
      const [nextLesson, nextMembers, nextStudios] = await Promise.all([
        fetchAdminLesson(tenantSlug, lessonId),
        fetchAdminMembers(tenantSlug),
        fetchAdminStudios(tenantSlug)
      ]);
      setLesson(nextLesson);
      setMembers(nextMembers);
      setStudios(nextStudios);
      setEditState(toEditState(nextLesson));
      setManualOccurrenceForm((current) => current.occurrenceDate ? current : createInitialManualOccurrenceFormState(nextLesson));
      try {
        setBillingFee(await fetchStudentBillingLessonFee(tenantSlug, lessonId));
        setBillingFeeUnavailable(false);
      } catch (billingError) {
        setBillingFee(null);
        setBillingFeeUnavailable(
          billingError instanceof ApiError && billingError.code === "STUDENT_BILLING_READ_DISABLED"
        );
      }
      setLoadState("idle");
    } catch (error) {
      if (isUnauthenticatedApiError(error)) {
        navigate(`/t/${tenantSlug}/entry`, { replace: true });
        return;
      }

      setLoadState("error");
      setLoadError(getProtectedApiErrorMessage(error, `${tenantLabels.lesson}詳細の取得に失敗しました`));
    }
  }

  useEffect(() => {
    if (!isSessionReady || !hasAdminRole) {
      return;
    }

    void refreshLesson();
  }, [hasAdminRole, isSessionReady, lessonId, tenantSlug]);

  async function handleSave(event: FormEvent<HTMLFormElement>) {
    event.preventDefault();

    if (!editState) {
      return;
    }

    setBusyKey("save");
    setLoadError(null);
    setFeedbackMessage(null);

    try {
      await patchAdminLesson(tenantSlug, lessonId, {
        title: editState.title.trim(),
        shortTitle: editState.shortTitle.trim() || null,
        description: editState.description.trim() || null,
        genre: editState.genre.trim() || null,
        targetLabel: editState.targetLabel.trim() || null,
        publicVisible: editState.publicVisible,
        trialPolicyId: editState.trialPolicyId.trim() || null,
        visibilityPolicyId: editState.visibilityPolicyId.trim() || null,
        sortOrder: Number.parseInt(editState.sortOrder, 10) || 0
      });
      await refreshLesson();
      setFeedbackMessage(`${tenantLabels.lesson}を更新しました。`);
    } catch (error) {
      if (isUnauthenticatedApiError(error)) {
        navigate(`/t/${tenantSlug}/entry`, { replace: true });
        return;
      }

      setLoadError(getProtectedApiErrorMessage(error, `${tenantLabels.lesson}の更新に失敗しました`));
    } finally {
      setBusyKey(null);
    }
  }

  async function handleTransition(nextStatus: LessonStatus) {
    setBusyKey(`transition:${nextStatus}`);
    setLoadError(null);
    setFeedbackMessage(null);

    try {
      await transitionAdminLesson(tenantSlug, lessonId, { toStatus: nextStatus });
      await refreshLesson();
      setFeedbackMessage(`${tenantLabels.lesson}の状態を「${describeStatus(nextStatus)}」に変更しました。`);
    } catch (error) {
      if (isUnauthenticatedApiError(error)) {
        navigate(`/t/${tenantSlug}/entry`, { replace: true });
        return;
      }

      setLoadError(getProtectedApiErrorMessage(error, `${tenantLabels.lesson}の状態変更に失敗しました`));
    } finally {
      setBusyKey(null);
    }
  }

  async function handleCreateManualOccurrence(event: FormEvent<HTMLFormElement>) {
    event.preventDefault();

    if (!lesson) {
      return;
    }

    setBusyKey("create-manual-occurrence");
    setLoadError(null);
    setFeedbackMessage(null);
    setLastManualOccurrenceYearMonth(null);

    try {
      const result = await createAdminManualOccurrence(tenantSlug, lessonId, {
        occurrenceDate: manualOccurrenceForm.occurrenceDate,
        startTime: manualOccurrenceForm.startTime,
        endTime: manualOccurrenceForm.endTime,
        studioId:
          manualOccurrenceForm.studioDecisionStatus === "tbd" ? null : manualOccurrenceForm.studioId || null,
        studioDecisionStatus: manualOccurrenceForm.studioDecisionStatus,
        teacherPlatformUserIds:
          manualOccurrenceForm.teacherAssignmentStatus === "tbd" ? [] : manualOccurrenceForm.teacherPlatformUserIds,
        teacherAssignmentStatus: manualOccurrenceForm.teacherAssignmentStatus,
        publicNote: manualOccurrenceForm.publicNote.trim() || null,
        operatorNote: manualOccurrenceForm.operatorNote.trim() || null,
        adminNote: manualOccurrenceForm.adminNote.trim() || null
      });
      setManualOccurrenceForm((current) => ({
        ...createInitialManualOccurrenceFormState(lesson),
        occurrenceDate: current.occurrenceDate
      }));
      setLastManualOccurrenceYearMonth(result.affectedYearMonths[0] ?? null);
      await refreshLesson();
      setFeedbackMessage(
        `個別予定を追加しました。(${result.items.length}件)`
      );
    } catch (error) {
      if (isUnauthenticatedApiError(error)) {
        navigate(`/t/${tenantSlug}/entry`, { replace: true });
        return;
      }

      setLoadError(getProtectedApiErrorMessage(error, "個別予定の追加に失敗しました"));
    } finally {
      setBusyKey(null);
    }
  }

  async function handleCancelManualOccurrence(occurrenceId: string) {
    setBusyKey(`cancel:${occurrenceId}`);
    setLoadError(null);
    setFeedbackMessage(null);

    try {
      await cancelAdminManualOccurrence(tenantSlug, occurrenceId);
      await refreshLesson();
      setFeedbackMessage("個別予定を取り消しました。");
    } catch (error) {
      if (isUnauthenticatedApiError(error)) {
        navigate(`/t/${tenantSlug}/entry`, { replace: true });
        return;
      }

      setLoadError(getProtectedApiErrorMessage(error, "個別予定の取消に失敗しました"));
    } finally {
      setBusyKey(null);
    }
  }

  return (
    <OperatorPageScaffold
      currentArea="admin"
      tenantSlug={tenantSlug}
      tenantName={session?.tenant.displayName}
      session={session}
      title={`${tenantLabels.lesson}詳細`}
      description={`${tenantLabels.lesson}の基本情報、状態、定期枠、個別予定を確認・編集します。`}
      links={buildAdminLinks(tenantLabels)}
    >
      <div style={{ display: "grid", gap: 16 }}>
        {routeStatus === "checking" || sessionStatus === "loading" ? <p>セッションを確認しています。</p> : null}
        {routeStatus === "redirecting" ? <p>利用開始画面へ移動しています。</p> : null}
        {sessionStatus === "error" ? <p>セッション確認に失敗しました。{errorMessage}</p> : null}
        {routeStatus === "authenticated" && !hasAdminRole ? <p>この画面は管理者のみ利用できます。</p> : null}
        {loadState === "loading" ? <p>{tenantLabels.lesson}詳細を読み込んでいます。</p> : null}
        {loadError ? <p>{loadError}</p> : null}
        {feedbackMessage ? <p>{feedbackMessage}</p> : null}
        {lastManualOccurrenceYearMonth ? (
          <div style={{ display: "grid", gap: 4, fontSize: 14 }}>
            <Link to={`/t/${tenantSlug}/teacher/admin/schedule-months/${lastManualOccurrenceYearMonth}`}>
              影響した{formatYearMonthLinkLabel(lastManualOccurrenceYearMonth)}を開く
            </Link>
            <span style={{ opacity: 0.8 }}>
              公開するには、スケジュール月で検証してから公開してください。
            </span>
          </div>
        ) : null}

        {isSessionReady && hasAdminRole && lesson && editState ? (
          <>
            <section style={{ display: "grid", gap: 8, padding: 12, border: "1px solid #e5e5e5", borderRadius: 12 }}>
              <strong>{lesson.title}</strong>
              <div style={{ fontSize: 14, opacity: 0.8 }}>
                {describeStatus(lesson.lessonKind)} / {describeStatus(lesson.status)} / {formatLessonDateRange(lesson.startDate, lesson.endDate)}
              </div>
              <div style={{ fontSize: 14, opacity: 0.8 }}>
                {lesson.defaultStudioName ?? `${tenantLabels.studio}未定`} / {lesson.primaryTeacherNameSnapshots.join(", ") || `${tenantLabels.teacher}未定`}
              </div>
              <div style={{ display: "flex", gap: 8, flexWrap: "wrap" }}>
                {lesson.affectedYearMonths.map((yearMonth) => (
                  <Link
                    key={yearMonth}
                    to={`/t/${tenantSlug}/teacher/admin/schedule-months/${yearMonth}`}
                    style={{ fontSize: 12 }}
                  >
                    {formatYearMonthLinkLabel(yearMonth)}
                  </Link>
                ))}
              </div>
            </section>

            <section style={{ display: "grid", gap: 8, padding: 12, border: "1px solid #e5e5e5", borderRadius: 12 }}>
              <h2 style={{ margin: 0, fontSize: 18 }}>請求料金</h2>
              {billingFee ? (
                <>
                  <strong>
                    {billingFee.current
                      ? billingFee.current.amount === 0
                        ? "無料（0円）"
                        : `${billingFee.current.amount.toLocaleString("ja-JP")}円${billingFee.lessonKind === "regular" ? " / 月" : " / コース総額"}`
                      : billingFee.configured ? "現在の適用期間外" : "未設定"}
                  </strong>
                  <Link to={`/t/${tenantSlug}/teacher/admin/billing/lesson-fees/${lesson.lessonId}`}>レッスン料金設定を開く</Link>
                </>
              ) : billingFeeUnavailable ? (
                <p style={{ margin: 0, fontSize: 14, opacity: 0.8 }}>生徒請求機能は現在無効です。レッスンの基本情報・予定管理には影響しません。</p>
              ) : (
                <p style={{ margin: 0, fontSize: 14, opacity: 0.8 }}>料金情報を確認できませんでした。レッスンの基本情報・予定管理は引き続き利用できます。</p>
              )}
            </section>

            <section style={{ display: "grid", gap: 12 }}>
              <h2 style={{ margin: 0, fontSize: 18 }}>状態変更</h2>
              <div style={{ display: "flex", gap: 8, flexWrap: "wrap" }}>
                {transitions.length === 0 ? <span>遷移候補はありません。</span> : null}
                {transitions.map((nextStatus) => (
                  <button
                    key={nextStatus}
                    type="button"
                    onClick={() => void handleTransition(nextStatus)}
                    disabled={busyKey === `transition:${nextStatus}`}
                  >
                    {busyKey === `transition:${nextStatus}` ? "更新中..." : `${describeStatus(nextStatus)}に変更`}
                  </button>
                ))}
              </div>
            </section>

            <section style={{ display: "grid", gap: 12 }}>
              <h2 style={{ margin: 0, fontSize: 18 }}>基本情報を編集</h2>
              <form
                onSubmit={(event) => void handleSave(event)}
                style={{ display: "grid", gap: 12, padding: 12, border: "1px solid #e5e5e5", borderRadius: 12 }}
              >
                <label style={{ display: "grid", gap: 4 }}>
                  <span>タイトル</span>
                  <input
                    value={editState.title}
                    onChange={(event) => setEditState((current) => current ? { ...current, title: event.target.value } : current)}
                  />
                </label>
                <label style={{ display: "grid", gap: 4 }}>
                  <span>短縮表示名</span>
                  <input
                    value={editState.shortTitle}
                    onChange={(event) =>
                      setEditState((current) => current ? { ...current, shortTitle: event.target.value } : current)
                    }
                  />
                </label>
                <label style={{ display: "grid", gap: 4 }}>
                  <span>説明</span>
                  <textarea
                    value={editState.description}
                    rows={3}
                    onChange={(event) =>
                      setEditState((current) => current ? { ...current, description: event.target.value } : current)
                    }
                  />
                </label>
                <div style={{ display: "grid", gap: 12, gridTemplateColumns: "repeat(auto-fit, minmax(140px, 1fr))" }}>
                  <label style={{ display: "grid", gap: 4 }}>
                    <span>ジャンル</span>
                    <input
                      value={editState.genre}
                      onChange={(event) =>
                        setEditState((current) => current ? { ...current, genre: event.target.value } : current)
                      }
                    />
                  </label>
                  <label style={{ display: "grid", gap: 4 }}>
                    <span>対象</span>
                    <input
                      value={editState.targetLabel}
                      onChange={(event) =>
                        setEditState((current) => current ? { ...current, targetLabel: event.target.value } : current)
                      }
                    />
                  </label>
                </div>
                <div style={{ display: "grid", gap: 12, gridTemplateColumns: "repeat(auto-fit, minmax(140px, 1fr))" }}>
                  <label style={{ display: "grid", gap: 4 }}>
                    <span>体験ポリシーID</span>
                    <input
                      value={editState.trialPolicyId}
                      onChange={(event) =>
                        setEditState((current) => current ? { ...current, trialPolicyId: event.target.value } : current)
                      }
                    />
                  </label>
                  <label style={{ display: "grid", gap: 4 }}>
                    <span>公開範囲ポリシーID</span>
                    <input
                      value={editState.visibilityPolicyId}
                      onChange={(event) =>
                        setEditState((current) =>
                          current ? { ...current, visibilityPolicyId: event.target.value } : current
                        )
                      }
                    />
                  </label>
                  <label style={{ display: "grid", gap: 4 }}>
                    <span>表示順</span>
                    <input
                      value={editState.sortOrder}
                      onChange={(event) =>
                        setEditState((current) => current ? { ...current, sortOrder: event.target.value } : current)
                      }
                    />
                  </label>
                </div>
                <label style={{ display: "flex", gap: 8, alignItems: "center" }}>
                  <input
                    type="checkbox"
                    checked={editState.publicVisible}
                    onChange={(event) =>
                      setEditState((current) => current ? { ...current, publicVisible: event.target.checked } : current)
                    }
                  />
                  <span>公開ページに表示</span>
                </label>
                <button type="submit" disabled={busyKey === "save"}>
                  {busyKey === "save" ? "保存中..." : `${tenantLabels.lesson}を更新`}
                </button>
              </form>
            </section>

            <section style={{ display: "grid", gap: 12 }}>
              <div style={{ display: "flex", justifyContent: "space-between", gap: 12, alignItems: "center", flexWrap: "wrap" }}>
                <h2 style={{ margin: 0, fontSize: 18 }}>定期枠</h2>
                {lesson.lessonKind === "regular" ? (
                  <Link to={`/t/${tenantSlug}/teacher/admin/lessons/${lesson.lessonId}/recurring-slots/new`}>
                    定期枠を追加
                  </Link>
                ) : null}
              </div>
              <p style={{ margin: 0, fontSize: 14, opacity: 0.8 }}>
                定期枠はこの{tenantLabels.lesson}詳細から管理できます。
                変更しても、個別追加・手動調整・上書き調整済みの予定は直接上書きされません。
                対象月で「予定を生成」を実行すると、自動生成された予定に反映されます。
              </p>
              <p style={{ margin: 0, fontSize: 14, opacity: 0.8 }}>
                定期枠を無効化すると、以後「予定を生成」を実行しても、その枠の予定は作成されません。
              </p>
              {lesson.recurringSlots.length === 0 ? <p style={{ margin: 0 }}>定期枠はありません。</p> : null}
              <div style={{ display: "grid", gap: 12 }}>
                {lesson.recurringSlots.map((slot) => (
                  <article
                    key={slot.slotId}
                    style={{ padding: 12, border: "1px solid #e5e5e5", borderRadius: 12, display: "grid", gap: 6 }}
                  >
                    <strong>{slot.startTime} - {slot.endTime}</strong>
                    <div style={{ fontSize: 14, opacity: 0.8 }}>
                      {getRecurrencePresetLabel(slot.recurrencePreset)} / {WEEKDAY_LABELS[slot.weekday] ?? slot.weekday}曜日 / {describeStatus(slot.status)}
                    </div>
                    <div style={{ fontSize: 14, opacity: 0.8 }}>
                      {slot.studioNameSnapshot ?? `${tenantLabels.studio}未定`} / {slot.teacherNameSnapshots.join(", ") || `${tenantLabels.teacher}未定`}
                    </div>
                    <div style={{ fontSize: 12, opacity: 0.7 }}>
                      適用開始 {formatTenantDate(slot.activeFrom)} / 適用終了 {formatOpenEndedDate(slot.activeTo)}
                    </div>
                    <div style={{ display: "flex", gap: 8, flexWrap: "wrap" }}>
                      <Link to={`/t/${tenantSlug}/teacher/admin/lessons/${lesson.lessonId}/recurring-slots/${slot.slotId}`}>
                        詳細・編集
                      </Link>
                    </div>
                  </article>
                ))}
              </div>
            </section>

            <section style={{ display: "grid", gap: 12 }}>
              <h2 style={{ margin: 0, fontSize: 18 }}>個別予定</h2>
              {lesson.manualOccurrences.length === 0 ? <p style={{ margin: 0 }}>個別予定はありません。</p> : null}
              <div style={{ display: "grid", gap: 12 }}>
                {lesson.manualOccurrences.map((occurrence) => (
                  <article
                    key={occurrence.occurrenceId}
                    style={{ padding: 12, border: "1px solid #e5e5e5", borderRadius: 12, display: "grid", gap: 6 }}
                  >
                    <Link to={`/t/${tenantSlug}/teacher/admin/occurrences/${occurrence.occurrenceId}`}>
                      {formatTenantIsoDate(occurrence.startAt)}
                    </Link>
                    <div style={{ fontSize: 14, opacity: 0.8 }}>
                      {formatTenantIsoTime(occurrence.startAt)} - {formatTenantIsoTime(occurrence.endAt)} / {describeStatus(occurrence.lifecycleStatus)}
                    </div>
                    <div style={{ fontSize: 14, opacity: 0.8 }}>
                      {occurrence.studioNameSnapshot ?? `${tenantLabels.studio}未定`} / {occurrence.teacherNameSnapshots.join(", ") || `${tenantLabels.teacher}未定`}
                    </div>
                    {occurrence.lifecycleStatus !== "cancelled" ? (
                      <button
                        type="button"
                        onClick={() => void handleCancelManualOccurrence(occurrence.occurrenceId)}
                        disabled={busyKey === `cancel:${occurrence.occurrenceId}`}
                      >
                        {busyKey === `cancel:${occurrence.occurrenceId}` ? "取消中..." : "個別予定を取消"}
                      </button>
                    ) : null}
                  </article>
                ))}
              </div>
            </section>

            <section style={{ display: "grid", gap: 12 }}>
              <h2 style={{ margin: 0, fontSize: 18 }}>個別予定を追加</h2>
              {lesson.lessonKind === "regular" && lesson.recurringSlots.length === 0 ? (
                <p style={{ margin: 0, fontSize: 14, opacity: 0.85 }}>
                  不定期{tenantLabels.lesson}はここから個別予定を追加してください。
                </p>
              ) : null}
              {lesson.lessonKind === "regular" ? (
                <p style={{ margin: 0, fontSize: 14, opacity: 0.85 }}>
                  発表会前の追加{tenantLabels.lesson}や、通常枠の外で行う 1 回分の予定をここから登録できます。
                </p>
              ) : null}
              <form
                onSubmit={(event) => void handleCreateManualOccurrence(event)}
                style={{ display: "grid", gap: 12, padding: 12, border: "1px solid #e5e5e5", borderRadius: 12 }}
              >
                <label style={{ display: "grid", gap: 4 }}>
                  <span>日付</span>
                  <input
                    type="date"
                    required
                    value={manualOccurrenceForm.occurrenceDate}
                    onChange={(event) =>
                      setManualOccurrenceForm((current) => ({ ...current, occurrenceDate: event.target.value }))
                    }
                  />
                </label>
                <div style={{ display: "grid", gap: 12, gridTemplateColumns: "repeat(auto-fit, minmax(140px, 1fr))" }}>
                  <label style={{ display: "grid", gap: 4 }}>
                    <span>開始時刻</span>
                    <input
                      type="time"
                      required
                      value={manualOccurrenceForm.startTime}
                      onChange={(event) =>
                        setManualOccurrenceForm((current) => ({ ...current, startTime: event.target.value }))
                      }
                    />
                  </label>
                  <label style={{ display: "grid", gap: 4 }}>
                    <span>終了時刻</span>
                    <input
                      type="time"
                      required
                      value={manualOccurrenceForm.endTime}
                      onChange={(event) =>
                        setManualOccurrenceForm((current) => ({ ...current, endTime: event.target.value }))
                      }
                    />
                  </label>
                </div>
                <label style={{ display: "grid", gap: 4 }}>
                  <span>{tenantLabels.studio}</span>
                  <select
                    value={manualOccurrenceForm.studioId}
                    onChange={(event) =>
                      setManualOccurrenceForm((current) => ({ ...current, studioId: event.target.value }))
                    }
                    disabled={manualOccurrenceForm.studioDecisionStatus === "tbd"}
                  >
                    <option value="">選択してください</option>
                    {studios?.items.map((studio) => (
                      <option key={studio.studioId} value={studio.studioId}>
                        {studio.displayName}
                      </option>
                    ))}
                  </select>
                </label>
                <label style={{ display: "flex", gap: 8, alignItems: "center" }}>
                  <input
                    type="checkbox"
                    checked={manualOccurrenceForm.studioDecisionStatus === "tbd"}
                    onChange={(event) =>
                      setManualOccurrenceForm((current) => ({
                        ...current,
                        studioDecisionStatus: event.target.checked ? "tbd" : "confirmed",
                        studioId: event.target.checked ? "" : current.studioId
                      }))
                    }
                  />
                  <span>{tenantLabels.studio}未定</span>
                </label>
                <fieldset style={{ display: "grid", gap: 8, border: "none", padding: 0, margin: 0 }}>
                  <legend style={{ padding: 0 }}>担当{tenantLabels.teacher}</legend>
                  <div style={{ display: "grid", gap: 8 }}>
                    {teacherMembers.map((member) => (
                      <label key={member.platformUserId} style={{ display: "flex", gap: 8, alignItems: "center" }}>
                        <input
                          type="checkbox"
                          checked={manualOccurrenceForm.teacherPlatformUserIds.includes(member.platformUserId)}
                          disabled={manualOccurrenceForm.teacherAssignmentStatus === "tbd"}
                          onChange={(event) =>
                            setManualOccurrenceForm((current) => ({
                              ...current,
                              teacherPlatformUserIds: toggleSelection(
                                current.teacherPlatformUserIds,
                                member.platformUserId,
                                event.target.checked
                              )
                            }))
                          }
                        />
                        <span>{getMemberDisplayName(member)}</span>
                      </label>
                    ))}
                  </div>
                </fieldset>
                <label style={{ display: "flex", gap: 8, alignItems: "center" }}>
                  <input
                    type="checkbox"
                    checked={manualOccurrenceForm.teacherAssignmentStatus === "tbd"}
                    onChange={(event) =>
                      setManualOccurrenceForm((current) => ({
                        ...current,
                        teacherAssignmentStatus: event.target.checked ? "tbd" : "assigned",
                        teacherPlatformUserIds: event.target.checked ? [] : current.teacherPlatformUserIds
                      }))
                    }
                  />
                  <span>{tenantLabels.teacher}未定</span>
                </label>
                <label style={{ display: "grid", gap: 4 }}>
                  <span>公開メモ</span>
                  <input
                    value={manualOccurrenceForm.publicNote}
                    onChange={(event) =>
                      setManualOccurrenceForm((current) => ({ ...current, publicNote: event.target.value }))
                    }
                  />
                </label>
                <label style={{ display: "grid", gap: 4 }}>
                  <span>運営メモ</span>
                  <input
                    value={manualOccurrenceForm.operatorNote}
                    onChange={(event) =>
                      setManualOccurrenceForm((current) => ({ ...current, operatorNote: event.target.value }))
                    }
                  />
                </label>
                <label style={{ display: "grid", gap: 4 }}>
                  <span>管理者メモ</span>
                  <input
                    value={manualOccurrenceForm.adminNote}
                    onChange={(event) =>
                      setManualOccurrenceForm((current) => ({ ...current, adminNote: event.target.value }))
                    }
                  />
                </label>
                <button type="submit" disabled={busyKey === "create-manual-occurrence"}>
                  {busyKey === "create-manual-occurrence" ? "追加中..." : "個別予定を追加"}
                </button>
                <div style={{ fontSize: 12, opacity: 0.8 }}>
                  追加後は{tenantLabels.lesson}詳細を再読み込みし、スケジュール月で検証して公開すると、一般・{tenantLabels.teacher}・公開ページへ反映されます。
                </div>
              </form>
            </section>
          </>
        ) : null}
      </div>
    </OperatorPageScaffold>
  );
}
